import React from "react";
import {
  PHASES,
  TIER_MODELS,
  scoreColor,
  scoreOf,
  tierAverage,
  phaseAverage,
} from "@/data/coachingData";
import { useI18n } from "@/i18n";
import { DimensionSummary } from "@/components/DimensionSummary";
import { CohortTable } from "@/components/CohortTable";
import { ThreeSixtyView } from "@/components/ThreeSixtyView";

export function ReportView({
  companyName,
  modelType,
  context,
  scores,
  teamScores = {},
  headcounts = {},
  recommendations,
  brandName,
  logoUrl,
  bookingUrl,
}) {
  const { t, locale } = useI18n();
  const tiers = TIER_MODELS[modelType] || TIER_MODELS["4-tier"];
  const date = new Date().toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });

  const tierName = (p) => {
    const key = `tier.${p.tier_key}`;
    const v = t(key);
    return v === key ? p.tier_label || "" : v;
  };

  return (
    <div id="report-content" className="max-w-4xl mx-auto space-y-8 print-text">
      <div className="flex items-center justify-between gap-4 pb-2">
        {logoUrl ? (
          <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5 print-logo">
            <img src={logoUrl} alt={brandName || "Brand"} className="h-9 max-w-[180px] object-contain" />
          </span>
        ) : (
          <span className="font-heading text-xl font-extrabold text-slate-900 print-text">
            {brandName || "CoachScore"}
          </span>
        )}
        <div className="flex items-center gap-3">
          {bookingUrl && (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="no-print inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold px-3.5 py-2 transition-colors duration-200"
              data-testid="report-booking-btn"
            >
              {t("report.book")}
            </a>
          )}
          <span className="text-xs text-slate-500">{date}</span>
        </div>
      </div>

      <div className="border-b border-slate-300 pb-6">
        <p className="text-xs uppercase tracking-widest font-semibold text-amber-600 mb-2">{t("report.kicker")}</p>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 print-text">{companyName}</h1>
        <p className="text-sm text-slate-600 mt-2">
          {t("report.modelLine", { model: t(`model.${modelType}`) })}
        </p>
        {context && <p className="text-sm text-slate-700 mt-3 leading-relaxed">{context}</p>}
      </div>

      {recommendations?.overall_summary && (
        <div className="print-card rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-2 print-text">
            {t("report.execSummary")}
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed print-text">{recommendations.overall_summary}</p>
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">{t("report.orgReadiness")}</p>
            <p className="font-heading text-2xl font-bold text-indigo-600">
              {recommendations.org_readiness_score ?? "—"}%
            </p>
          </div>
        </div>
      )}

      <div className="print-card rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-1 print-text">
          {t("report.scorecard")}
        </h2>
        <p className="text-xs text-slate-500 mb-4">{t("report.scorecardSub")}</p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="report-5d-table">
            <thead>
              <tr className="border-b border-slate-300">
                <th className="text-left py-2 pr-3 text-slate-600 font-semibold">{t("report.competency")}</th>
                {tiers.map((tier) => (
                  <th key={tier.key} className="text-center py-2 px-2 text-slate-600 font-medium text-xs">
                    {t(`tierShort.${tier.key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PHASES.map((phase) => (
                <React.Fragment key={phase.key}>
                  <tr className="bg-slate-50">
                    <td className="py-2 pr-3 text-[11px] uppercase tracking-widest font-bold text-amber-600 print-text">
                      {t(`phase.${phase.key}.title`)}
                    </td>
                    {tiers.map((tier) => (
                      <td
                        key={tier.key}
                        className="text-center py-2 px-2 font-mono text-xs font-bold"
                        style={{ color: scoreColor(phaseAverage(scores, tier.key, phase.key)) }}
                      >
                        {phaseAverage(scores, tier.key, phase.key).toFixed(1)}
                      </td>
                    ))}
                  </tr>
                  {phase.comps.map((comp) => (
                    <tr key={comp.key} className="border-b border-slate-200">
                      <td className="py-2 pr-3 text-slate-800 print-text">
                        <span className="text-[9px] uppercase tracking-widest font-bold text-indigo-600/80 mr-1.5">
                          {t(`dim.${comp.dim}`)}
                        </span>
                        {t(`comp.${comp.key}`)}
                      </td>
                      {tiers.map((tier) => {
                        const v = scoreOf(scores, tier.key, comp.key);
                        return (
                          <td
                            key={tier.key}
                            className="text-center py-2 px-2 font-mono print-text"
                            style={{ color: scoreColor(v) }}
                          >
                            {v}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="border-t-2 border-slate-300">
                <td className="py-2.5 pr-3 font-semibold text-slate-900 print-text">{t("report.levelAverage")}</td>
                {tiers.map((tier) => (
                  <td
                    key={tier.key}
                    className="text-center py-2.5 px-2 font-mono font-bold"
                    style={{ color: scoreColor(tierAverage(scores, tier.key)) }}
                  >
                    {tierAverage(scores, tier.key).toFixed(1)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          {["strong", "potential", "gap"].map((b) => (
            <div
              key={b}
              className="rounded-lg border p-3"
              style={{
                borderColor: `${scoreColor(b === "strong" ? 5 : b === "potential" ? 3.5 : 2)}55`,
                background: `${scoreColor(b === "strong" ? 5 : b === "potential" ? 3.5 : 2)}12`,
              }}
            >
              <p
                className="text-[11px] uppercase tracking-widest font-bold mb-1"
                style={{ color: scoreColor(b === "strong" ? 5 : b === "potential" ? 3.5 : 2) }}
              >
                {b === "strong" ? "4 – 5" : b === "potential" ? "3 – 3,9" : `0 – 2,9`} · {t(`band.${b}`)}
              </p>
              <p className="text-[11px] text-slate-600 leading-snug print-text">{t(`band.${b}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="print-card rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-1 print-text">
          {t("cohort.title")}
        </h2>
        <p className="text-xs text-slate-500 mb-4">{t("cohort.sub")}</p>
        <CohortTable tiers={tiers} scores={scores} headcounts={headcounts} />
      </div>

      <div>
        <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-1 print-text">
          {t("dim.summaryTitle")}
        </h2>
        <p className="text-xs text-slate-500 mb-4">{t("dim.summarySub")}</p>
        <DimensionSummary tiers={tiers} scores={scores} insight={recommendations?.dimension_insight} compact />
      </div>

      {Object.keys(teamScores || {}).length > 0 && (
        <div>
          <h2 className="font-heading text-base md:text-lg font-bold text-slate-900 mb-1 print-text">
            {t("t360.title")}
          </h2>
          <p className="text-xs text-slate-500 mb-4">{t("t360.sub")}</p>
          <ThreeSixtyView tiers={tiers} scores={scores} teamScores={teamScores} />
        </div>
      )}

      {recommendations?.programs?.map((p, i) => (
        <div key={i} className="print-card rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-amber-600">{tierName(p)}</p>
              <h3 className="font-heading text-base md:text-lg font-bold text-slate-900 print-text">
                {p.program_name}
              </h3>
            </div>
            <span className="text-xs font-semibold text-slate-700">
              {t("report.priority", { priority: t(`priority.${p.priority}`) })}
            </span>
          </div>
          <p className="text-sm text-slate-700 mb-3 print-text">{p.rationale}</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <ReportKV label={t("rec.format")} value={p.format} />
            <ReportKV label={t("rec.duration")} value={t("report.durationShort", { n: p.duration_months })} />
          </div>
          {p.modules?.length > 0 && (
            <ul className="mt-3 list-disc list-inside text-sm text-slate-700 space-y-1 print-text">
              {p.modules.map((m, mi) => (
                <li key={mi}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {!recommendations && <p className="text-center text-slate-500 py-8">{t("report.empty")}</p>}
    </div>
  );
}

function ReportKV({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-slate-900 font-medium print-text">{value}</p>
    </div>
  );
}
